import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, FileText, UserCog, Upload, Plus, CalendarDays } from "lucide-react";
import { CATEGORY_DESCRIPTIONS, type PackageDefinition } from "@/config/packages";
import { useDynamicPricing } from "@/hooks/useDynamicPricing";

interface PackageSelectorProps {
  onPackageSelect: (packageId: string) => void;
  selectedPackage?: string;
  onUploadClick?: () => void;
  onSubscriptionChange?: (quantities: Record<string, number>) => void;
}

type Category = 'S' | 'M' | 'L';

export function PackageSelector({
  onPackageSelect,
  selectedPackage,
  onUploadClick,
  onSubscriptionChange
}: PackageSelectorProps) {
  const categories: Category[] = ['S', 'M', 'L'];
  const { packages: dynamicPackages, subscriptions: dynamicSubscriptions, deliverables: dynamicDeliverables, subscriptionsEnabled } = useDynamicPricing();
  const [aboQuantities, setAboQuantities] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('selectedSubscriptionQuantities');
      return saved ? JSON.parse(saved) : {};
    } catch {return {};}
  });

  const getPackageForCategory = (category: Category): PackageDefinition | undefined => {
    return dynamicPackages.find((p) => p.category === category);
  };

  const handleCategorySelect = (category: Category) => {
    const pkg = getPackageForCategory(category);
    if (pkg && !pkg.disabled) {
      onPackageSelect(pkg.id);
    }
  };

  const getSelectedCategory = (): Category | null => {
    if (!selectedPackage) return null;
    const pkg = dynamicPackages.find((p) => p.id === selectedPackage);
    return pkg?.category || null;
  };

  const updateAboQty = (id: string, delta: number) => {
    setAboQuantities((prev) => {
      const updated = { ...prev, [id]: Math.max(0, (prev[id] || 0) + delta) };
      localStorage.setItem('selectedSubscriptionQuantities', JSON.stringify(updated));
      onSubscriptionChange?.(updated);
      return updated;
    });
  };

  useEffect(() => {
    if (Object.values(aboQuantities).some((q) => q > 0)) {
      onSubscriptionChange?.(aboQuantities);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCategory = getSelectedCategory();

  return (
    <div className="space-y-6">
      {/* Package Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => {
          const pkg = getPackageForCategory(category);
          const isSelected = selectedCategory === category;
          const catInfo = CATEGORY_DESCRIPTIONS[category];
          const isDisabled = pkg?.disabled ?? false;
          const deliverables = dynamicDeliverables[category];

          if (!pkg) return null;

          return (
            <Card
              key={category}
              className={`relative transition-all duration-300 cursor-pointer overflow-hidden ${
              isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg hover:border-primary'} ${
              isSelected ? 'ring-2 ring-primary shadow-lg' : 'border-border'}`}
              onClick={() => !isDisabled && handleCategorySelect(category)}>

              {pkg.popular && !isDisabled &&
              <div className="absolute top-3 -right-6 w-24 bg-green-700 text-white text-center py-0.5 transform rotate-45 shadow-lg z-20 text-[10px] font-bold border-y border-green-800">
                  BELIEBT
                </div>
              }

              <CardHeader className="text-center pb-2 pt-4 overflow-hidden">
                <div className={`w-12 h-12 mx-auto rounded-xl ${catInfo.color} ${catInfo.textColor} flex items-center justify-center font-bold text-xl mb-2`}>
                  {category}
                </div>
                <CardTitle className="text-lg font-bold">{catInfo.name}</CardTitle>
                <div className="text-2xl font-bold text-primary mt-2">
                  {pkg.priceDisplay}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{pkg.description}</p>
              </CardHeader>

              <CardContent className="px-4 pb-4">
                <div className="mb-3">
                  <div className="flex items-center gap-1 mb-1">
                    <FileText className="w-3 h-3 text-primary" />
                    <span className="text-xs font-semibold text-foreground">Sie erhalten:</span>
                  </div>
                  <ul className="space-y-0.5">
                    {deliverables.youGet.map((item, idx) =>
                    <li key={idx} className="flex items-start text-xs">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="mb-3">
                  <div className="flex items-center gap-1 mb-1">
                    <UserCog className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs font-semibold text-foreground">Ihr Aufwand:</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-1 ${
                    category === 'S' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                    category === 'M' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`
                    }>
                      {deliverables.effortLabel}
                    </span>
                  </div>
                  <ul className="space-y-0.5">
                    {deliverables.youDo.map((item, idx) => {
                      if (typeof item === 'string') {
                        return (
                          <li key={idx} className="flex items-start text-xs">
                            <span className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5 flex items-center justify-center text-orange-500">•</span>
                            <span className="text-muted-foreground">{item}</span>
                          </li>);
                      } else {
                        return (
                          <li key={idx} className="text-xs">
                            {item.main &&
                            <div className="flex items-start">
                                <span className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5 flex items-center justify-center text-orange-500">•</span>
                                <span className="text-muted-foreground">{item.main}</span>
                              </div>
                            }
                            <ul className="ml-4 mt-0.5 space-y-0.5">
                              {item.sub.map((subItem, subIdx) =>
                              <li key={subIdx} className="flex items-start">
                                  <span className="w-2 h-2 mr-1 flex-shrink-0 mt-1 flex items-center justify-center text-orange-400 text-[8px]">○</span>
                                  <span className="text-muted-foreground text-[10px]">{subItem}</span>
                                </li>
                              )}
                            </ul>
                          </li>);
                      }
                    })}
                  </ul>
                </div>

                {isSelected ?
                <Button variant="selected" size="sm" className="w-full mt-3">
                    ✓ {pkg.name} ausgewählt
                  </Button> :
                isDisabled ?
                <Button variant="outline" size="sm" className="w-full mt-3" disabled>
                    Bald verfügbar
                  </Button> :
                <Button variant="outline" size="sm" className="w-full mt-3">
                    Auswählen
                  </Button>
                }
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Abo-Box – nur wenn Abos aktiviert sind */}
      {subscriptionsEnabled && (
        <Card className="border-border bg-card text-card-foreground shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground">Optional: Karten-Abo dazubuchen</h3>
            </div>

            <p className="text-xs text-muted-foreground mb-4">
              <strong className="text-foreground">Hinweis:</strong> In Ihrem Abo erhalten Sie die Leistungen Ihres gewählten Pakets.
              {selectedCategory === 'S' && ' Bei Paket S: Analysebericht (PDF) (ausgenommen Handlungsempfehlungen).'}
              {selectedCategory === 'M' && ' Bei Paket M: Analysebericht (PDF) + Allergenliste (Excel) (ausgenommen Handlungsempfehlungen).'}
              {selectedCategory === 'L' && ' Bei Paket L: Analysebericht (PDF) + separate Allergenliste (Excel) (ausgenommen Handlungsempfehlungen und überarbeitete Karte).'}
              {!selectedCategory && ' Wählen Sie oben ein Paket, um die enthaltenen Leistungen zu sehen.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {dynamicSubscriptions.map((sub) => {
                const qty = aboQuantities[sub.id] || 0;
                return (
                  <div key={sub.id} className={`relative p-3 rounded-lg border text-sm transition-all ${qty > 0 ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    {/* Preis rechts oben */}
                    <div className="absolute top-2 right-3">
                      <span className="text-lg font-bold text-primary">{sub.priceDisplay}</span>
                    </div>

                    {/* Titel */}
                    <div className="flex items-center gap-2 mb-1 pr-24">
                      <span className="text-lg">{sub.icon}</span>
                      <span className="font-medium text-foreground">{sub.shortName}</span>
                    </div>

                    {/* Beschreibung + Limits links, Menge rechts */}
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-[10px] text-muted-foreground">{sub.description}</p>
                        <span className="text-[10px] text-muted-foreground">Max. {sub.maxItemsPerUpload} Artikel pro Upload · {sub.monthlyUploadLimit} Uploads/Monat</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateAboQty(sub.id, -1)} disabled={qty === 0}>
                          −
                        </Button>
                        <span className="font-bold w-4 text-center text-xs">{qty}</span>
                        <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateAboQty(sub.id, 1)}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Upload-Button */}
            {onUploadClick &&
              <div className="flex justify-center mt-5">
                <Button variant="default" size="lg" onClick={onUploadClick} disabled={!selectedPackage}>
                  <Upload className="w-4 h-4 mr-2" />
                  Speisekarte hochladen
                </Button>
              </div>
            }
          </CardContent>
        </Card>
      )}

      {/* Upload-Button wenn Abos deaktiviert */}
      {!subscriptionsEnabled && onUploadClick && (
        <div className="flex justify-center mt-5">
          <Button variant="default" size="lg" onClick={onUploadClick} disabled={!selectedPackage}>
            <Upload className="w-4 h-4 mr-2" />
            Speisekarte hochladen
          </Button>
        </div>
      )}
    </div>
  );
}
